import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";

export function LearnHub() {
  useSeo({
    title: "Learn the VA Claims Process | Veterans Disability Education",
    description: "Plain-language education on service connection, evidence, lay statements, nexus letters, and C&P exams — from a veteran who's been through it.",
  });
  const [articles, setArticles] = useState(null);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState("all");

  useEffect(() => {
    api.get("/articles/categories").then((r) => setCategories(r.data));
    api.get("/articles").then((r) => setArticles(r.data));
  }, []);

  const load = async (cat) => {
    setActive(cat);
    const { data } = await api.get(`/articles${cat === "all" ? "" : `?category=${encodeURIComponent(cat)}`}`);
    setArticles(data);
  };

  if (!articles) return <div className="grid place-items-center h-96"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div>
      <section className="bg-primary text-primary-foreground">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-16">
          <div className="label-caps text-accent mb-4">Learn</div>
          <h1 className="font-display text-4xl lg:text-6xl leading-[1.05] max-w-4xl">
            Plain-language teaching that <span className="text-accent">demystifies</span> a complex system.
          </h1>
          <p className="mt-5 text-lg text-primary-foreground/85 max-w-2xl">
            Real education, not clickbait. Read one article a week and you'll know more than 90% of first-time VA filers.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-12">
        <div className="flex flex-wrap gap-2 mb-10" data-testid="category-filter">
          <button
            onClick={() => load("all")}
            className={`px-4 py-2 font-semibold text-sm border-2 ${active === "all" ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border hover:border-primary text-primary"}`}
            data-testid="cat-all"
          >All</button>
          {categories.map((c) => (
            <button key={c} onClick={() => load(c)}
              className={`px-4 py-2 font-semibold text-sm border-2 ${active === c ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border hover:border-primary text-primary"}`}
              data-testid={`cat-${c.toLowerCase()}`}
            >{c}</button>
          ))}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
          {articles.map((a) => (
            <Link to={`/learn/${a.slug}`} key={a.slug} className="group block" data-testid={`article-${a.slug}`}>
              <div className="aspect-[4/3] overflow-hidden bg-secondary mb-5">
                {a.cover_url && <img src={a.cover_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
              </div>
              {a.category && <Badge className="mb-3 bg-secondary text-primary hover:bg-secondary border-0 uppercase text-[10px] tracking-widest font-semibold">{a.category}</Badge>}
              <h2 className="font-display text-xl leading-snug">{a.title}</h2>
              <p className="text-[15px] text-primary/80 mt-2 leading-relaxed">{a.excerpt}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary group-hover:text-accent-foreground">
                Read article <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          ))}
        </div>
        {articles.length === 0 && <div className="text-center text-primary/60 py-16">No articles in this category yet.</div>}
      </div>
    </div>
  );
}

export function LearnArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  useEffect(() => { api.get(`/articles/${slug}`).then((r) => setArticle(r.data)); }, [slug]);
  if (!article) return <div className="grid place-items-center h-96"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div>
      <article className="mx-auto max-w-3xl px-5 py-14">
        <Link to="/learn" className="inline-flex items-center gap-2 text-sm text-primary/70 hover:text-primary font-semibold" data-testid="learn-back">
          <ArrowLeft className="w-4 h-4" /> Back to Learn
        </Link>
        {article.category && <Badge className="mt-8 mb-4 bg-primary text-primary-foreground hover:bg-primary uppercase text-[10px] tracking-widest">{article.category}</Badge>}
        <h1 className="font-display text-3xl lg:text-5xl leading-[1.05] tracking-tight">{article.title}</h1>
        {article.cover_url && <img src={article.cover_url} alt="" className="mt-10 w-full aspect-[16/9] object-cover" />}
        <div className="prose mt-10 text-lg leading-relaxed text-primary/90 whitespace-pre-wrap">{article.body}</div>
      </article>

      <section className="bg-secondary mt-16">
        <div className="mx-auto max-w-3xl px-5 py-14 text-center">
          <div className="gold-rule mx-auto mb-6"></div>
          <h2 className="font-display text-2xl lg:text-4xl mb-4">Ready to apply what you just learned?</h2>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg"><Link to="/calculator">Try the calculator</Link></Button>
            <Button asChild variant="outline" size="lg"><Link to="/intake">Start your free consult</Link></Button>
          </div>
        </div>
      </section>
    </div>
  );
}
